/**
 * In-memory copy of the Tauri store at `tmp.json`.
 * The same rules live in lobbyze-web
 * `apps/notification-cards/src/host/native/shared-memory.ts`.
 *
 * `config.json` is the durable settings file. Session keys (the selected
 * team, the session blob, grind lists) go in `tmp.json` instead.
 *
 * Every window loads that same store. The plugin keeps one map for the
 * process, and `entries()` reads it. A `store://change` event updates one
 * key sooner. The one-second read replaces an event that arrived before
 * this window was listening, and a read that raced a write.
 */

export const TMP_STORE_PATH = "tmp.json";

/** How often a window re-reads the store after the first copy. */
export const REFRESH_INTERVAL_MS = 1_000;

const YIELD_AFTER_MISMATCHES = 5;

export type SharedEntry = {
  key: string;
  value: string;
};

export class SharedCache {
  private values = new Map<string, string>();
  private readonly pending = new Map<string, { value: string; mismatches: number }>();
  private revision = 0;
  private readonly changedAt = new Map<string, number>();

  get(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  revisionAt(): number {
    return this.revision;
  }

  /** Local write. Kept until a later read of the store contains it. */
  remember(key: string, value: string): void {
    this.values.set(key, value);
    this.pending.set(key, { value, mismatches: 0 });
  }

  pendingValue(key: string): string | undefined {
    return this.pending.get(key)?.value;
  }

  pendingWrites(): SharedEntry[] {
    return Array.from(this.pending, ([key, pending]) => ({
      key,
      value: pending.value,
    }));
  }

  /**
   * One key from `store://change`.
   * A local write still waiting to show up in the store is left in place.
   */
  applyRemote(key: string, value: string | null): void {
    const pending = this.pending.get(key);
    if (pending) {
      if (value === pending.value) {
        this.pending.delete(key);
        this.values.set(key, value);
        this.noteChange(key);
      }
      return;
    }
    if (value == null) {
      if (!this.values.has(key)) {
        return;
      }
      this.values.delete(key);
      this.noteChange(key);
      return;
    }
    if (this.values.get(key) === value) {
      return;
    }
    this.values.set(key, value);
    this.noteChange(key);
  }

  /**
   * Replace the cache with a full `entries()` read.
   * `readRevision` is the revision captured before that read started.
   * A change that arrived while the read was in flight is kept.
   * Local writes missing from the read are returned so the caller can
   * send them again. A write that is still missing after several reads
   * yields to the store.
   */
  applyEntries(
    entries: ReadonlyArray<readonly [string, unknown]>,
    readRevision: number,
  ): SharedEntry[] {
    const remote = new Map<string, string>();
    for (const pair of entries) {
      if (pair == null) {
        continue;
      }
      const [key, value] = pair;
      if (typeof key !== "string" || typeof value !== "string") {
        continue;
      }
      remote.set(key, value);
    }
    for (const [key, at] of this.changedAt) {
      if (at <= readRevision) {
        continue;
      }
      const live = this.values.get(key);
      if (live === undefined) {
        remote.delete(key);
      } else {
        remote.set(key, live);
      }
    }
    this.changedAt.clear();

    const retry: SharedEntry[] = [];
    for (const [key, pending] of this.pending) {
      if (remote.get(key) === pending.value) {
        this.pending.delete(key);
        continue;
      }
      pending.mismatches += 1;
      if (pending.mismatches > YIELD_AFTER_MISMATCHES) {
        this.pending.delete(key);
        continue;
      }
      remote.set(key, pending.value);
      retry.push({ key, value: pending.value });
    }
    this.values = remote;
    return retry;
  }

  private noteChange(key: string): void {
    this.revision += 1;
    this.changedAt.set(key, this.revision);
  }
}

/**
 * A store value this cache understands.
 * A string is stored. `null` means the key was removed.
 * Anything else is ignored.
 */
export function readStoreValue(value: unknown): string | null | undefined {
  if (typeof value === "string") {
    return value;
  }
  if (value == null) {
    return null;
  }
  return undefined;
}

/** Delay before trying to open the store again. */
export function nextLoadDelayMs(attempt: number): number {
  const step = Math.max(0, Math.floor(attempt));
  return Math.min(1_000, 100 * 2 ** Math.min(step, 4));
}
